import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import LoadingBarComponent from '../components/LoadingBar';
import Table from '../components/Table';
import PieChartSection from '../components/PieChartSection';
import Widget from '../components/Widget';
import CollapsibleSection from '../components/CollapsibleSection';
import DoubleColumnTable from '../components/DoubleColumnTable';
import CollapsibleLoadingBar from '../components/AnimatedCollapsibleLoadingBar';
import DynamicFirewallAccordion from '../components/FirewallVulnSection';
import DynamicNetworkAccordion from '../components/NetworkVulnSection';
import DynamicWebAccordion from '../components/WebsiteVulnSection';
import { apiRequest } from '../utils/api';
import styled from 'styled-components';
import {
  Terminal,
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Info,
  Globe,
  Clock,
  Target,
  Activity,
  Loader2,
  Download,
  CheckCircle,
  Trash2
} from 'lucide-react';

const ScanHeader = styled.div`
  background: linear-gradient(
    90deg,
    #00419F 0%,
    #1562B7 50%,
    #1E90FF 100%
  );
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  h2 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .asset-name {
    font-size: 1.75rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    margin-left: 0.5rem;
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;

const ScanInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 1.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.25rem;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    opacity: 0.9;
  }

  .info-content {
    flex: 1;
  }

  label {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    display: block;
    margin-bottom: 0.25rem;
  }

  span {
    font-size: 1.125rem;
    color: white;
    font-weight: 600;
    display: block;
  }
`;

const ProgressContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1.25rem;
  border-radius: 12px;
  margin-top: 1.5rem;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  .progress-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: white;
    font-weight: 500;
  }

  .progress-percentage {
    background: ${(props) =>
      props.status === 'Terminated'
        ? 'rgba(239, 68, 68, 0.25)'
        : props.progress >= 100
        ? 'rgba(30, 144, 255, 0.25)'
        : 'rgba(255, 255, 255, 0.2)'};
    padding: 0.25rem 1rem;
    border-radius: 9999px;
    font-weight: 600;
    font-size: 0.875rem;
    transition: background-color 0.5s ease;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 9999px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  width: ${(props) => props.progress}%;
  height: 100%;
  background: ${(props) => (props.status === 'Terminated' ? '#ef4444' : '#50a14f')};
  border-radius: 9999px;
  transition: width 0.5s ease, background 0.5s ease;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${(props) => {
      if (props.status === 'Terminated') {
        return `
          background: none;
          animation: none;
        `;
      }
      if (props.progress >= 100) {
        return `
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.1),
            rgba(255, 255, 255, 0.3),
            rgba(255, 255, 255, 0.1)
          );
          animation: pulse 2s ease-in-out infinite;
        `;
      }
      return `
        background: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 0.15),
          rgba(255, 255, 255, 0)
        );
        animation: shimmer 2s infinite linear;
      `;
    }}
    background-size: 200% 100%;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SpinningLoader = styled(Loader2)`
  color: #1562B7;
  animation: spin 1.5s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CompletedIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1E90FF;
  animation: fadeIn 0.5s ease-in-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.8); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SEVERITY_COLORS = {
  Critical: '#3e090a',
  High: '#d32f2f',
  Medium: '#ffc324',
  Low: '#2e7d32',
  Informational: '#3c99f4',
};

const WidgetBox = styled.div`
  background: ${(props) => SEVERITY_COLORS[props.severity]};
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const WidgetTitle = styled.div`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.025em;
`;

const WidgetValue = styled.p`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  line-height: 1;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  p {
    color: black;
    font-size: 1.125rem;
    font-weight: 500;
    margin: 1rem 0 0;
  }
`;

const ReportActions = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const VersionBadge = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const DownloadButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-decoration: none;

  &:hover {
    background: ${props => !props.disabled && 'rgba(255, 255, 255, 0.25)'};
    transform: ${props => !props.disabled && 'translateY(-2px)'};
  }

  &:active {
    transform: ${props => !props.disabled && 'translateY(0)'};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${props => props.disabled && `
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const TerminateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) =>
    props.status === 'Terminated' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.15)'};
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  cursor: ${(props) => (props.status === 'Terminated' ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-decoration: none;
  opacity: ${(props) => (props.status === 'Terminated' ? 0.7 : 1)};

  &:hover {
    background: ${(props) =>
      props.status === 'Terminated' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.25)'};
    transform: ${(props) => (props.status === 'Terminated' ? 'none' : 'translateY(-2px)')};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const theme = document.body.getAttribute('data-theme');
  const { scanType, scanId } = useParams();
  const [scanData, setScanData] = useState(null);
  const [vulnerabilityScanData, setVulnerabilityScanData] = useState([]);
  const [firewallVulnerabilityData, setFirewallVulnerabilityData] = useState([]);
  const [networkVulnerabilityData, setNetworkVulnerabilityData] = useState([]);
  const [webVulnerabilityData, setWebVulnerabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'Critical':
        return <AlertTriangle color="white" />;
      case 'High':
        return <ShieldAlert color="white" />;
      case 'Medium':
        return <Shield color="white" />;
      case 'Low':
        return <ShieldCheck color="white" />;
      case 'Informational':
        return <Info color="white" />;
      default:
        return <Shield color="white" />;
    }
  };

  const fetchScanData = async () => {
    try {
      const response = await apiRequest(`/api/report/${scanType}/${scanId}`, { method: 'GET' });
      const data = await response.json();
      setScanData(data.scanData || []);
      setVulnerabilityScanData(data.vulnerabilityScanData || []);
      setFirewallVulnerabilityData(data.firewallVulnerabilityScanData || []);
      setNetworkVulnerabilityData(data.networkVulnerabilityScanData || []);
      setWebVulnerabilityData(data.vulnerabilityScanData || []);
    } catch (error) {
      console.error("Error fetching scan data:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScanData();
    const intervalId = setInterval(fetchScanData, 10000);
    return () => clearInterval(intervalId);
  }, [scanType, scanId]);

  const handleTerminateScan = async () => {
    if (!vulnerabilityScanData || vulnerabilityScanData.length === 0) return;

    const currentVuln = vulnerabilityScanData[0];
    if (currentVuln.status === 'Terminated') return;

    if (!window.confirm('Are you sure you want to terminate this scan? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);

      const response = await apiRequest('/api/scan/terminate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          containerId: currentVuln.containerId
        }),
      });

      const result = await response.json();
      if (response.ok) {
        window.location.href = '/reports';
      } else {
        alert(result.message || 'Failed to terminate scan');
      }
    } catch (error) {
      console.error('Termination error:', error);
      alert('An error occurred while terminating the scan');
    } finally {
      setLoading(false);
    }
  };

  const getAssetName = () => {
    if (!scanData || !scanData[0]) return 'N/A';
    return (
      scanData[0].asset.name ||
      scanData[0].assetName ||
      scanData[0].asset?.name ||
      'N/A'
    );
  };

  const isWebAsset = () => {
    if (!scanData || !scanData[0] || !scanData[0].asset) return false;
    return scanData[0].asset.assetType === 'website';
  };

  if (loading && !scanData) {
    return (
      <LoadingContainer>
        <SpinningLoader size={48} />
        <p>Loading report, please wait...</p>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <AlertTriangle size={48} color="#ef4444" />
        <p>Error: {error}</p>
      </LoadingContainer>
    );
  }

  if (!scanData || scanData.length === 0) {
    return (
      <LoadingContainer>
        <SpinningLoader size={48} />
        <p>No scan data available.</p>
      </LoadingContainer>
    );
  }

  const currentScan = scanData[0];
  const status = currentScan.status;
  const progress = currentScan.progress || 0;

  return (
    <div className="p-6">
      <ScanHeader>
        <HeaderTitle>
          <Terminal />
          <div className="title-wrapper">
            <h2>Vulnerability Assessment Report</h2>
          </div>
        </HeaderTitle>

        <ReportActions>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <VersionBadge>
              {currentScan.version || '1.0'}
            </VersionBadge>
            <TerminateButton
              onClick={handleTerminateScan}
              status={status}
              disabled={status === 'Terminated'}
            >
              <Trash2 size={16} />
              {status === 'Terminated' ? 'Terminated' : 'Terminate'}
            </TerminateButton>
          </div>
          <DownloadButton 
            to={isWebAsset() ? `/export/${scanType}/${scanId}` : '#'}
            disabled={!isWebAsset()}
          >
            <Download />
            Download Report
            {/* {!isWebAsset() && (
              <span style={{ 
                fontSize: '0.75rem',
                marginLeft: '0.5rem',
                opacity: 0.8
              }}>
                (Web only)
              </span>
            )} */}
          </DownloadButton>
        </ReportActions>

        <ScanInfo>
          <InfoCard>
            <InfoItem>
              <Target />
              <div className="info-content">
                <label>Asset Name</label>
                <span>{getAssetName()}</span>
              </div>
            </InfoItem>
          </InfoCard>
          <InfoCard>
            <InfoItem>
              <Activity />
              <div className="info-content">
                <label>Status</label>
                <span>{status || 'N/A'}</span>
              </div>
            </InfoItem>
          </InfoCard>
          <InfoCard>
            <InfoItem>
              <Globe />
              <div className="info-content">
                <label>Target URL</label>
                <span>{currentScan.url || 'N/A'}</span>
              </div>
            </InfoItem>
          </InfoCard>
        </ScanInfo>

        <ProgressContainer>
          <ProgressLabel progress={progress} status={status}>
            <div className="progress-title">
              {status === 'Terminated' ? (
                <AlertTriangle size={16} />
              ) : progress >= 100 ? (
                <CompletedIcon>
                  <CheckCircle size={16} />
                </CompletedIcon>
              ) : (
                <SpinningLoader size={16} />
              )}
              <span>
                {status === 'Terminated'
                  ? 'Scan Terminated'
                  : progress >= 100
                  ? 'Scan Completed'
                  : 'Scan Progress'}
              </span>
            </div>
            <span className="progress-percentage">
              {status === 'Terminated' ? '—' : `${progress}%`}
            </span>
          </ProgressLabel>
          <ProgressBar>
            <ProgressFill progress={progress} status={status} />
          </ProgressBar>
        </ProgressContainer>
      </ScanHeader>

      <WidgetGrid>
        {['Critical', 'High', 'Medium', 'Low', 'Informational'].map((severity) => (
          <WidgetBox key={severity} severity={severity}>
            <HeaderContainer>
              <WidgetTitle>{severity}</WidgetTitle>
              {getSeverityIcon(severity)}
            </HeaderContainer>
            <WidgetValue>
              {currentScan.scanResult?.[severity] || 0}
            </WidgetValue>
          </WidgetBox>
        ))}
      </WidgetGrid>

      {firewallVulnerabilityData.length > 0 && (
        <DynamicFirewallAccordion data={firewallVulnerabilityData} />
      )}
      {networkVulnerabilityData.length > 0 && (
        <DynamicNetworkAccordion data={networkVulnerabilityData} />
      )}
      {webVulnerabilityData.length > 0 && (
        <DynamicWebAccordion data={webVulnerabilityData} />
      )}
    </div>
  );
};

export default AnalyticsPage;